'use client';

import LandingLayout from '@/components/LandingLayout';
import {
  ArrowRightIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  HeartIcon,
  HomeIcon,
  LanguageIcon,
  NoSymbolIcon,
  PhoneIcon,
  PlayIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContextNext';
import { trackButtonClick, trackPageView, trackPhoneClick } from '../../lib/analytics';
import { getPlanDisplayPrice, PRICING_PLANS } from '../../types/pricing';

export default function FeaturesPage() {
  useEffect(() => {
    trackPageView('Features Page', undefined, { funnel_step: 'consideration' });
  }, []);

  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const starterDisplayPrice = getPlanDisplayPrice(PRICING_PLANS[0], pricingContext);

  const sampleCalls = useMemo(() => [
    {
      id: 1,
      phoneNumber: '(415) 877-3166',
      duration: '2:34',
      summary: 'Appointment booking - scheduled for Tuesday 3pm',
      timestamp: '10:30 AM'
    },
    {
      id: 2,
      phoneNumber: '(415) 877-3166',
      duration: '1:45',
      summary: 'Service inquiry - provided pricing information',
      timestamp: '11:15 AM'
    },
    {
      id: 3,
      phoneNumber: '(415) 877-3166',
      duration: '3:12',
      summary: 'Appointment reschedule - moved to Friday 2pm',
      timestamp: '12:20 PM'
    },
    {
      id: 4,
      phoneNumber: '(555) 321-0987',
      duration: '0:58',
      summary: 'Hours inquiry - provided weekend availability',
      timestamp: '1:05 PM'
    }
  ], []);

  useEffect(() => {
    const animateCards = () => {
      sampleCalls.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 800);
      });
    };

    const interval = setInterval(() => {
      setVisibleCards([]);
      setTimeout(animateCards, 500);
    }, 8000);

    animateCards();

    return () => clearInterval(interval);
  }, [sampleCalls]);

  const pillars = [
    {
      icon: PhoneIcon,
      title: 'AI Call Handling',
      points: [
        'Answers every call in under 2 seconds with human-like conversation',
        'Understands caller intent and guides them to book, get info, or leave messages',
      ],
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Knowledge & FAQs',
      points: [
        'Accurate answers about your hours, services, pricing, and policies',
        'Easy setup - just add your business info and common questions',
      ],
    },
    {
      icon: DocumentTextIcon,
      title: 'Transcripts & Summaries',
      points: [
        'Full transcripts and concise AI summaries after every call',
        'Playback and searchable logs for quality and training',
      ],
    },
    {
      icon: CheckCircleIcon,
      title: 'Appointment Scheduling',
      points: [
        'Books appointments instantly while customers are on the phone',
        'Syncs with Google Calendar and prevents double-bookings',
      ],
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Routing & Escalation',
      points: [
        'Escalate complex requests: transfer live, send SMS/email, or create a follow-up',
        'Customizable after-hours and emergency handling',
      ],
    },
    {
      icon: LanguageIcon,
      title: 'Voices & Languages',
      points: [
        'Choose natural voices and accents to match your brand',
        'Multilingual understanding and responses',
      ],
    },
    {
      icon: NoSymbolIcon,
      title: 'Spam Detection & Blocking',
      points: [
        'Automatic protection against 10.5M+ known spam numbers with real-time detection',
        'Manually block any number from call history or dedicated spam page with optional notes',
      ],
    },
    {
      icon: ShieldCheckIcon,
      title: 'Security & Compliance',
      points: [
        'Enterprise-grade security with clear auditability via call logs',
        'Privacy-aware configurations for regulated sectors',
      ],
    },
  ];

  const industries = [
    {
      title: 'Healthcare',
      description:
        'Complex booking rules, cancellations, and pre-visit screening with privacy-aware workflows.',
      icon: HeartIcon,
      gradient: 'from-red-500 to-pink-600'
    },
    {
      title: 'Home Services',
      description:
        'Estimate questions, appointment windows, and priority routing for emergencies and VIPs.',
      icon: HomeIcon,
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Salons & Retail',
      description:
        'Service menus, stylist/technician preferences, quick rescheduling, and no-show handling.',
      icon: WrenchScrewdriverIcon,
      gradient: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Professional Services',
      description:
        'Client intake, availability, conflict checks, and routing to specialists or teams.',
      icon: BuildingOffice2Icon,
      gradient: 'from-green-500 to-emerald-600'
    },
  ];

  return (
    <LandingLayout>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-gradient-to-l from-pink-500/15 to-indigo-500/15 blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent pb-8 leading-tight">
              Never miss a customer again
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-10 leading-relaxed">
              Your AI phone receptionist answers every call instantly, books appointments, and handles customer questions - even when you're busy, sleeping, or on vacation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                href="/register"
                onClick={() => trackButtonClick("try_greet_free", "features_page_hero")}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-blue-500/25"
              >
                Start Free 7-Day Trial
              </Link>
              <a
                href="tel:+14158773166"
                onClick={() => trackPhoneClick('+14158773166', 'features_page_hero')}
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 px-10 py-5 rounded-2xl font-bold text-lg transition-all duration-300 inline-flex items-center hover:scale-105"
              >
                Call Live Demo
                <ArrowRightIcon className="h-5 w-5 ml-2" />
              </a>
            </div>
            {/* Quick highlights */}
            <div className="flex flex-wrap gap-4">
              <span className="inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md px-6 py-3 text-base font-semibold text-white border border-white/20">
                <div className="p-1 bg-blue-500 rounded-lg">
                  <BoltIcon className="h-4 w-4 text-white" />
                </div>
                24/7 instant answer
              </span>
              <span className="inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md px-6 py-3 text-base font-semibold text-white border border-white/20">
                <div className="p-1 bg-green-500 rounded-lg">
                  <CheckCircleIcon className="h-4 w-4 text-white" />
                </div>
                5-minute setup
              </span>
              <span className="inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md px-6 py-3 text-base font-semibold text-white border border-white/20">
                <div className="p-1 bg-purple-500 rounded-lg">
                  <ChartBarIcon className="h-4 w-4 text-white" />
                </div>
                Free 7-day trial
              </span>
              <span className="inline-flex items-center gap-3 rounded-2xl bg-white/10 backdrop-blur-md px-6 py-3 text-base font-semibold text-white border border-white/20">
                <div className="p-1 bg-yellow-500 rounded-lg">
                  <CheckCircleIcon className="h-4 w-4 text-white" />
                </div>
                Cancel anytime
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison: Voicemail vs Greet */}
      <section id="voicemail-comparison" className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent pb-6">Never Miss a Lead Again</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Every missed call costs you money. Stop losing customers to voicemail - Greet answers every call instantly and turns inquiries into bookings.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {/* Traditional Voicemail */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white/80 backdrop-blur-sm border border-red-100 rounded-3xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-red-100 rounded-2xl">
                    <XCircleIcon className="h-8 w-8 text-red-600" />
                  </div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-red-700 to-red-500 bg-clip-text text-transparent">Traditional Voicemail</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-red-100 rounded-full">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-gray-700 font-medium">67% of callers hang up without leaving a message</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-red-100 rounded-full">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-gray-700 font-medium">No instant answers or scheduling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-red-100 rounded-full">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-gray-700 font-medium">No qualification or routing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-red-100 rounded-full">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Delayed follow-up and manual effort</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-red-100 rounded-full">
                      <XCircleIcon className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-gray-700 font-medium">No instant booking - customers call competitors instead</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Greet AI Receptionist */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all"></div>
              <div className="relative bg-white/80 backdrop-blur-sm border border-green-100 rounded-3xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-green-700 to-blue-700 bg-clip-text text-transparent">Greet AI Receptionist</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Answers immediately, every time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Qualifies intent and captures contact info</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Books appointments or shares info on the spot</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Sends summaries and transcripts to your team</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 p-1 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-medium">Always on - after-hours included</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature pillars */}
      <section id="capabilities" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent pb-6">Everything you need to handle customer calls</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Your complete AI phone receptionist - sounds human, works 24/7, and starts helping customers from day one.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => {
              const gradientColors = [
                'from-blue-500 to-purple-600',
                'from-green-500 to-teal-600', 
                'from-purple-500 to-pink-600',
                'from-orange-500 to-red-600',
                'from-indigo-500 to-blue-600',
                'from-teal-500 to-green-600'
              ];
              const bgGradients = [
                'from-blue-50 to-purple-50',
                'from-green-50 to-teal-50',
                'from-purple-50 to-pink-50', 
                'from-orange-50 to-red-50',
                'from-indigo-50 to-blue-50',
                'from-teal-50 to-green-50'
              ];
              return (
                <div key={pillar.title} className="group relative">
                  <div className={`absolute inset-0 bg-gradient-to-r ${gradientColors[index % gradientColors.length]} opacity-0 group-hover:opacity-10 rounded-3xl blur-xl transition-all duration-300`}></div>
                  <div className={`relative p-8 rounded-3xl border border-gray-100 hover:border-gray-200 bg-gradient-to-br ${bgGradients[index % bgGradients.length]} hover:shadow-2xl hover:-translate-y-2 transition-all duration-300`}>
                    <div className={`mb-6 inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-r ${gradientColors[index % gradientColors.length]} shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                      <pillar.icon className="h-8 w-8 text-white transition-transform duration-300 group-hover:scale-125" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 transition-colors duration-300 group-hover:text-blue-600">{pillar.title}</h3>
                    <ul className="space-y-3">
                      {pillar.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3">
                          <div className="mt-1 p-1 bg-green-100 rounded-full transition-colors duration-300 group-hover:bg-green-200">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 transition-colors duration-300 group-hover:text-green-700" />
                          </div>
                          <span className="text-gray-700 font-medium leading-relaxed transition-colors duration-300 group-hover:text-gray-900">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust & reliability section */}
      <section id="trust" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent pb-6">Sounds so human, customers prefer it</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Most customers don't even realize they're talking to AI - they just think you have an amazing receptionist.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">99.2%</div>
              <div className="text-gray-600">Call success rate</div>
              <div className="text-sm text-gray-500 mt-1">Handles questions correctly</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">2 sec</div>
              <div className="text-gray-600">Average answer time</div>
              <div className="text-sm text-gray-500 mt-1">Never keeps customers waiting</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600">Always available</div>
              <div className="text-sm text-gray-500 mt-1">Even holidays and weekends</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Common concerns answered</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">"Will customers know it's AI?"</h4>
                  <p className="text-gray-600">Our AI voices are so natural that most customers don't realize they're talking to AI. They just think you have excellent customer service.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">"What if it can't answer something?"</h4>
                  <p className="text-gray-600">When AI can't help, it politely takes a message and sends you the details immediately. No customer is left hanging.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">"Is it reliable for my business?"</h4>
                  <p className="text-gray-600">Greet handles thousands of calls daily with 99.2% success rate. It's more reliable than human staff and never calls in sick.</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">What customers say:</h4>
              <blockquote className="text-gray-700 italic mb-4">
                "I had no idea I was talking to AI until they told me! The voice sounded completely natural and answered all my questions about their services. I booked my appointment right on the call."
              </blockquote>
              <div className="text-sm text-gray-600">- Sarah M., customer calling a plumbing company</div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry solutions */}
      <section id="industries" className="py-24 bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 to-slate-700 bg-clip-text text-transparent pb-6">Tailored for your industry</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Purpose-built flows for the businesses that depend on phone calls.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((card) => (
              <div key={card.title} className="group relative">
                <div className={`absolute inset-0 bg-gradient-to-r ${card.gradient} opacity-0 group-hover:opacity-10 rounded-3xl blur-xl transition-all duration-300`}></div>
                <div className="relative bg-white/80 backdrop-blur-sm border border-gray-200 rounded-3xl p-8 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                  <div className={`mb-6 inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-r ${card.gradient} shadow-lg`}>
                    <card.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{card.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logging & analytics highlight */}
      <section id="analytics" className="py-24 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:pr-8">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-900 via-purple-800 to-pink-800 bg-clip-text text-transparent pb-6">Every conversation captured</h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Transparency by default: transcripts, recordings, and structured summaries give you a clear record of every call.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-4">
                  <div className="mt-1 p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg">
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-gray-700 font-semibold text-lg">Outcome tracking and intent analytics</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-1 p-2 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-xl shadow-lg">
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-gray-700 font-semibold text-lg">Searchable call history and quality review</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="mt-1 p-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl shadow-lg">
                    <CheckCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-gray-700 font-semibold text-lg">Alerts and summaries to your team via SMS or email</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-3xl blur-xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm border border-indigo-200 rounded-3xl h-80 p-6 overflow-hidden shadow-2xl">
              <div className="space-y-3 h-full">
                {sampleCalls.map((call, index) => (
                  <div
                    key={call.id}
                    className={`transform transition-all duration-700 ease-out ${
                      visibleCards.includes(index)
                        ? 'translate-x-0 opacity-100'
                        : 'translate-x-full opacity-0'
                    }`}
                  >
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <PhoneIcon className="h-4 w-4 text-blue-500" />
                          <span className="text-sm font-medium text-gray-900">{call.phoneNumber}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">{call.duration}</span>
                          <button className="p-1 hover:bg-gray-100 rounded">
                            <PlayIcon className="h-3 w-3 text-gray-600" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 truncate">{call.summary}</p>
                      <div className="text-xs text-gray-400 mt-1">{call.timestamp}</div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Questions about getting started</h2>
            <p className="text-lg text-gray-600">Everything you need to know about trying Greet risk-free.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">How fast can I get set up?</h3>
              <p className="text-gray-700">Under 5 minutes. Just enter your business info, connect your calendar, and you're live. No technical setup required.</p>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Can I try it risk-free?</h3>
              <p className="text-gray-700">Yes! Get a free 7-day trial. No contracts, no setup fees, cancel anytime - even during your trial.</p>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">How much does it cost?</h3>
              <p className="text-gray-700">
                Plans start at{' '}
                {starterDisplayPrice.showSale ? (
                  <>
                    <span className="line-through text-gray-400">{starterDisplayPrice.basePrice}</span>{' '}
                    <span className="font-semibold text-green-700">{starterDisplayPrice.price}</span>
                  </>
                ) : (
                  starterDisplayPrice.price
                )}
                {' '}for 50 calls. Much cheaper than hiring a receptionist (which costs $30,000+ annually).
              </p>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Can I use my existing phone number?</h3>
              <p className="text-gray-700">Absolutely. Keep your current number by forwarding it to Greet, or get a new local number for free.</p>
            </div>
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">How does spam protection work?</h3>
              <p className="text-gray-700">Greet automatically blocks notifications from 10.5M+ known spam numbers. Spam calls are still logged for your records, but you won't receive alerts. You can also manually block any number anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Start your free trial today</h2>
          <p className="text-xl text-blue-100 mb-8">Try Greet risk-free for 7 days. Setup takes 5 minutes - be answering calls within the hour.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/register"
              onClick={() => trackButtonClick("start_free_trial", "features_page_cta_bottom")}
              className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Start 7-Day Free Trial
            </Link>
            <a
              href="tel:+14158773166"
              onClick={() => trackPhoneClick('+14158773166', 'features_page_cta_bottom')}
              className="bg-transparent border border-white text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Call Live Demo: (415) 877-3166
            </a>
          </div>
          <p className="text-blue-200 text-sm mt-4">No contracts • Cancel anytime • Setup in 5 minutes</p>
        </div>
      </section>
    </LandingLayout>
  );
}

